import { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { formatCurrency } from '../../../utils/formatCurrency';

export interface BankPaymentFeeAdjustModalProps {
  open: boolean;
  rail: 'eft' | 'payshap';
  feeAmount: number;
  availableBalance: number;
  maxAffordableAmount: number;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
}

function railLabel(rail: 'eft' | 'payshap'): string {
  return rail === 'payshap' ? 'Instant Payment' : 'Standard EFT';
}

export function BankPaymentFeeAdjustModal({
  open,
  rail,
  feeAmount,
  availableBalance,
  maxAffordableAmount,
  onConfirm,
  onCancel,
  isProcessing = false,
}: BankPaymentFeeAdjustModalProps) {
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const canAdjust = maxAffordableAmount >= 1;

  useEffect(() => {
    if (open && cancelBtnRef.current) {
      const id = window.setTimeout(() => {
        cancelBtnRef.current?.focus();
      }, 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const feeText = formatCurrency(feeAmount);
  const balanceText = formatCurrency(availableBalance);
  const maxText = formatCurrency(maxAffordableAmount);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !isProcessing) onCancel(); }}>
      <DialogContent className="max-w-sm mx-auto" aria-describedby="bank-fee-adjust-description">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '20px', fontWeight: 700 }}>
            {canAdjust ? 'Amount exceeds available balance' : 'Insufficient balance'}
          </DialogTitle>
          <DialogDescription id="bank-fee-adjust-description" className="sr-only">
            Transaction fee reduces the maximum amount you can send from your wallet balance.
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-lg border p-3 flex gap-2 items-start"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            backgroundColor: canAdjust ? '#fffbeb' : '#fee2e2',
            borderColor: canAdjust ? '#f59e0b' : '#dc2626',
            color: canAdjust ? '#92400e' : '#991b1b',
          }}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p style={{ fontSize: '14px', lineHeight: 1.5, margin: 0 }}>
            {canAdjust ? (
              <>
                The {railLabel(rail)} transaction fee is <strong>{feeText}</strong>.
                With your available balance of <strong>{balanceText}</strong>, the maximum you can send is{' '}
                <strong>{maxText}</strong>. Would you like to change the amount and continue?
              </>
            ) : (
              <>
                The {railLabel(rail)} transaction fee is <strong>{feeText}</strong>, which exceeds your
                available balance of <strong>{balanceText}</strong>. Please top up your wallet before sending
                money to a bank account.
              </>
            )}
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            ref={cancelBtnRef}
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 text-white"
            style={{
              backgroundColor: '#dc2626',
              fontFamily: 'Montserrat, sans-serif',
              minHeight: '48px',
              fontWeight: 600,
            }}
          >
            {canAdjust ? 'No' : 'Close'}
          </Button>
          {canAdjust && (
            <Button
              onClick={onConfirm}
              disabled={isProcessing}
              className="flex-1 text-white"
              style={{
                backgroundColor: '#86BE41',
                fontFamily: 'Montserrat, sans-serif',
                minHeight: '48px',
                fontWeight: 600,
              }}
            >
              Yes, use max amount
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
